import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';
import {
  Download,
  Trash2,
  CheckCircle2,
  CloudOff,
  HardDrive,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import {
  storeBulkTranslation,
  listOfflineManifests,
  removeOfflineTranslation,
  estimateOfflineSize,
} from '../utils/offlineBibleStore';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * "Download for Offline" panel — lists fully-seeded translations the user
 * can download into IndexedDB for zero-network reading. Shows per-translation
 * status (Downloaded / Available / Downloading…) and a global storage meter.
 *
 * Backend endpoints:
 *   GET /api/bible/offline/available  -> translation list + verse counts
 *   GET /api/bible/bulk?translation=X -> flat list of all verses
 */
const OfflineBibleSettings = ({ language = 'en' }) => {
  const [available, setAvailable] = useState([]);
  const [manifests, setManifests] = useState([]);
  // Per-translation download state so multiple downloads can run in parallel.
  // Shape: { [translation]: { progress: number, error?: string } }
  const [downloadState, setDownloadState] = useState({});
  const [loading, setLoading] = useState(true);
  const [storage, setStorage] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [avail, list] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/bible/offline/available`),
        listOfflineManifests(),
      ]);
      setAvailable(avail.data.translations || []);
      setManifests(list || []);
      setStorage(await estimateOfflineSize());
    } catch (e) {
      console.error('offline refresh', e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const downloadedSet = new Set(manifests.map((m) => m.translation));
  const isDownloadingAny = Object.keys(downloadState).length > 0;

  const handleDownload = async (translation) => {
    if (downloadState[translation]) return; // already running
    setDownloadState((s) => ({ ...s, [translation]: { progress: 5 } }));
    try {
      const verses = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('GET', `${BACKEND_URL}/api/bible/bulk?translation=${translation}`);
        xhr.responseType = 'json';
        xhr.onprogress = (e) => {
          const pct = e.lengthComputable
            ? Math.round((e.loaded / e.total) * 70)
            : null;
          setDownloadState((s) =>
            s[translation]
              ? {
                  ...s,
                  [translation]: {
                    progress: pct ?? Math.min(65, (s[translation].progress || 0) + 2),
                  },
                }
              : s
          );
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve((xhr.response && xhr.response.verses) || []);
          } else {
            reject(new Error(`HTTP ${xhr.status}`));
          }
        };
        xhr.onerror = () => reject(new Error('Network error'));
        xhr.send();
      });

      if (!verses.length) throw new Error('Empty response');
      setDownloadState((s) =>
        s[translation] ? { ...s, [translation]: { progress: 75 } } : s
      );
      await storeBulkTranslation(translation, verses);
      setDownloadState((s) =>
        s[translation] ? { ...s, [translation]: { progress: 100 } } : s
      );

      toast.success(
        language === 'es'
          ? `${translation}: ${verses.length.toLocaleString()} versículos guardados`
          : `${translation}: ${verses.length.toLocaleString()} verses cached for offline`
      );
      await refresh();
    } catch (err) {
      console.error('offline download', err);
      toast.error(
        language === 'es'
          ? `Error al descargar ${translation}`
          : `Failed to download ${translation}`
      );
    } finally {
      setDownloadState((s) => {
        const { [translation]: _removed, ...rest } = s;
        return rest;
      });
    }
  };

  // Fire-and-forget "Download all" — kicks off remaining translations in
  // parallel (but at most 3 concurrent XHRs so we don't blow the HTTP/1.1
  // per-host limit on older browsers).
  const handleDownloadAll = () => {
    const remaining = available
      .map((t) => t.translation)
      .filter((t) => !downloadedSet.has(t) && !downloadState[t]);
    if (!remaining.length) return;

    const MAX_CONCURRENT = 3;
    const queue = [...remaining];

    const runNext = async () => {
      const next = queue.shift();
      if (!next) return;
      await handleDownload(next);
      await runNext();
    };

    for (let i = 0; i < Math.min(MAX_CONCURRENT, queue.length); i++) {
      runNext();
    }
  };

  const handleRemove = async (translation) => {
    await removeOfflineTranslation(translation);
    toast.info(
      language === 'es'
        ? `${translation} eliminada del dispositivo`
        : `${translation} removed from device`
    );
    await refresh();
  };

  const fmtBytes = (b) => {
    if (!b || b < 1024) return `${b || 0} B`;
    if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
    return `${(b / 1024 / 1024).toFixed(1)} MB`;
  };

  const fmtKb = (kb) =>
    kb >= 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`;

  return (
    <Card data-testid="offline-bible-settings">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-amber-500/10 rounded-lg">
            <CloudOff className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <CardTitle className="text-lg">
              {language === 'es' ? 'Biblia sin conexión' : 'Offline Bible'}
            </CardTitle>
            <CardDescription>
              {language === 'es'
                ? 'Descarga traducciones completas para leer sin internet.'
                : 'Download full translations to read without an internet connection.'}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading && (
          <div className="py-8 text-center text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
            {language === 'es' ? 'Cargando…' : 'Loading…'}
          </div>
        )}

        {!loading && available.length === 0 && (
          <p className="text-sm text-muted-foreground">
            {language === 'es'
              ? 'No hay traducciones disponibles sin conexión.'
              : 'No translations are currently available offline.'}
          </p>
        )}

        {/* Bulk "Download all" shortcut */}
        {!loading && available.length > 0 &&
          available.some((t) => !downloadedSet.has(t.translation)) && (
          <div className="flex items-center justify-end pb-1">
            <Button
              variant="secondary"
              size="sm"
              onClick={handleDownloadAll}
              disabled={isDownloadingAny}
              data-testid="offline-download-all-btn"
            >
              <Download className="w-3.5 h-3.5 mr-1" />
              {language === 'es' ? 'Descargar todas' : 'Download all'}
            </Button>
          </div>
        )}

        <ul className="divide-y divide-border">
          {available.map((t) => {
            const downloaded = downloadedSet.has(t.translation);
            const dl = downloadState[t.translation];
            const isDownloading = !!dl;
            const progress = dl?.progress || 0;
            return (
              <li
                key={t.translation}
                className="py-3 flex items-center justify-between gap-3"
                data-testid={`offline-row-${t.translation}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold">{t.translation}</span>
                    {downloaded && (
                      <Badge
                        variant="outline"
                        className="bg-emerald-500/15 text-emerald-400 border-emerald-500/30 text-xs"
                      >
                        <CheckCircle2 className="w-3 h-3 mr-1" />
                        {language === 'es' ? 'Descargada' : 'Downloaded'}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    {t.verses.toLocaleString()}{' '}
                    {language === 'es' ? 'versículos' : 'verses'} • ~
                    {fmtKb(t.approx_kb)}
                  </p>
                  {isDownloading && (
                    <div className="mt-2 space-y-1">
                      <Progress value={progress} className="h-1.5" />
                      <p className="text-[10px] text-muted-foreground">
                        {language === 'es' ? 'Descargando…' : 'Downloading…'} {progress}%
                      </p>
                    </div>
                  )}
                </div>
                <div className="flex gap-2 shrink-0">
                  {downloaded ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemove(t.translation)}
                      disabled={isDownloading}
                      data-testid={`offline-remove-${t.translation}`}
                    >
                      <Trash2 className="w-3.5 h-3.5 mr-1" />
                      {language === 'es' ? 'Quitar' : 'Remove'}
                    </Button>
                  ) : (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(t.translation)}
                      disabled={isDownloading}
                      data-testid={`offline-download-${t.translation}`}
                    >
                      {isDownloading ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                      ) : (
                        <Download className="w-3.5 h-3.5 mr-1" />
                      )}
                      {language === 'es' ? 'Descargar' : 'Download'}
                    </Button>
                  )}
                </div>
              </li>
            );
          })}
        </ul>

        {storage && (
          <div className="flex items-center gap-2 pt-3 border-t text-xs text-muted-foreground">
            <HardDrive className="w-3.5 h-3.5" />
            {language === 'es' ? 'Almacenamiento usado' : 'Storage used'}:
            &nbsp;
            <span className="font-medium text-foreground">
              {fmtBytes(storage.usage)}
            </span>
            {storage.quota ? ` / ${fmtBytes(storage.quota)}` : ''}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default OfflineBibleSettings;
